/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import {
    m11ReportContainerFoiChanged, m11ReportContainerResourceChanged, m11ReportSection01FoiChanged,
    m11ReportSection01ResourceChanged, selectM11ReportContainerFoi, selectM11ReportContainerResource,
    selectM11ReportSection01Foi, selectM11ReportSection01Resource, selectM11ResearchStudyResource
} from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import jsonEquals from "../../functions/jsonEquals";
import getIdFromReference from "../../functions/getIdFromReference";
import loadResource from "../m11ReportFunctions/loadResource";
import TextDivEntry from "./TextDivEntry";
import { simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRQuantity } from "../../datatypeComponents/QuantityEntry";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";

const saveOverallDesignChanges = async (dispatch, apiToken, m11ResearchStudyFoi,
    section112State, setAlertModalState, setStartingSection112State) => {

    let changesStates = {
        section112State: section112State
    };
    let response = await saveM11Changes(apiToken, 16, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ReportContainerResource) {
            dispatch(m11ReportContainerResourceChanged(response.m11ReportContainerResource));
        }
        if (response.m11ReportSection01Resource) {
            dispatch(m11ReportSection01ResourceChanged(response.m11ReportSection01Resource));
        }
        setStartingSection112State(JSON.parse(JSON.stringify(section112State)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const M11Step016OverallDesign = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ReportContainerFoi = useSelector(selectM11ReportContainerFoi);
    const m11ReportContainerResource = useSelector(selectM11ReportContainerResource);
    const m11ReportSection01Foi = useSelector(selectM11ReportSection01Foi);
    const m11ReportSection01Resource = useSelector(selectM11ReportSection01Resource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [section112State, setSection112State] = useState({
        "status": "empty", "section112Xhtml": ""
    });
    const [startingSection112State, setStartingSection112State] = useState({
        "status": "empty", "section112Xhtml": ""
    });
    const [initialCalculationNeeded, setInitialCalculationNeeded] = useState(false);

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && !jsonEquals(section112State, startingSection112State)),
        async saveAllChanges() {
            await saveOverallDesignChanges(dispatch, apiToken, m11ResearchStudyFoi,
                section112State, setAlertModalState, setStartingSection112State);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource?.id) {
            let m11ReportFoi = "";
            if (m11ResearchStudyResource.relatesTo?.length > 0) {
                for (let relatesTo of m11ResearchStudyResource.relatesTo) {
                    if (relatesTo.type?.coding?.[0]?.code === "supported-with" && relatesTo.type.text === "M11 Report" &&
                        relatesTo.targetReference) {
                        m11ReportFoi = getIdFromReference(relatesTo.targetReference);
                    }
                }
            }
            if (m11ReportFoi && !isNaN(m11ReportFoi) && m11ReportFoi !== m11ReportContainerFoi) {
                dispatch(m11ReportContainerFoiChanged(m11ReportFoi));
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ResearchStudyResource]);

    useEffect(() => {
        if (initialCalculationNeeded === true) {
            let productCodes = [];
            let productNames = [];
            let populationType = "";
            let interventionModel = "";
            let populationDiagnosis = "";
            let controlType = "";
            let minimumAge = "";
            let maximumAge = "";
            let controlDescription = "";
            let siteDistribution = "";
            let geographicScope = "";
            let interventionAssignmentMethod = "";
            let randomisationType = "";
            let stratificationIndicator = "";
            let masterProtocolIndicator = "";
            let drugDeviceComboIndicator = "";
            let adaptiveTrialIndicator = "";
            if (m11ResearchStudyResource.focus?.length) {
                for (const focus of m11ResearchStudyResource.focus) {
                    if (focus.concept?.coding?.[0]?.system === "ForUseWithM11ReportAuthoringTool" &&
                        focus.concept.text) {
                        if (focus.concept.coding[0].display === "Sponsor's Product Code") {
                            productCodes.push(focus.concept.text);
                        } else if (focus.concept.coding[0].display === "Product Nonproprietary Name") {
                            productNames.push(focus.concept.text);
                        }
                    }
                }
            }
            if (m11ResearchStudyResource.classifier?.length > 0) {
                for (const classifier of m11ResearchStudyResource.classifier) {
                    if (classifier.coding?.[0]?.system === "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl") {
                        if (classifier.coding[0].code === "C218503" ||
                            classifier.coding[0].code === "C218504") {
                            populationType = classifier.coding[0].display;
                        } else if (classifier.coding[0].code === "C82640" ||
                            classifier.coding[0].code === "C82639" ||
                            classifier.coding[0].code === "C82637" ||
                            classifier.coding[0].code === "C82638" ||
                            classifier.coding[0].code === "C142568" ||
                            (classifier.coding[0].code === "C17649" && classifier.coding[0].display === "Other Intervention Model (C17649)")) {
                            interventionModel = classifier.coding[0].display;
                        } else if (classifier.coding[0].code === "C49648" ||
                            classifier.coding[0].code === "C49649" ||
                            classifier.coding[0].code === "C120841" ||
                            classifier.coding[0].code === "C218505" ||
                            classifier.coding[0].code === "C218506" ||
                            classifier.coding[0].code === "C184727" ||
                            classifier.coding[0].code === "C28280") {
                            controlType = classifier.coding[0].display;
                        } else if (classifier.coding[0].code === "C217004" ||
                            classifier.coding[0].code === "C217005") {
                            siteDistribution = classifier.coding[0].display;
                        } else if (classifier.coding[0].code === "C217006" ||
                            classifier.coding[0].code === "C217007") {
                            geographicScope = classifier.coding[0].display;
                        } else if (classifier.coding[0].code === "C25196" ||
                            classifier.coding[0].code === "C222801" ||
                            (classifier.coding[0].code === "C17649" && classifier.coding[0].display === "Other Intervention Assignment Method (C17649)")) {
                            interventionAssignmentMethod = classifier.coding[0].display;
                            if (classifier.text) {
                                randomisationType = classifier.text;
                            }
                        }
                    } else if (classifier.coding?.[0]?.system === "ForUseWithM11ReportAuthoringTool") {
                        if (classifier.coding[0].display === "Control Description") {
                            controlDescription = classifier.text;
                        } else if (classifier.coding[0].display === "Stratification Indicator") {
                            if (classifier.text === "True") {
                                stratificationIndicator = "Yes (C49488)";
                            } else if (classifier.text === "False") {
                                stratificationIndicator = "No (C49487)";
                            }
                        } else if (classifier.coding[0].display === "Master Protocol Indicator") {
                            if (classifier.text === "True") {
                                masterProtocolIndicator = "Yes (C49488)";
                            } else if (classifier.text === "False") {
                                masterProtocolIndicator = "No (C49487)";
                            }
                        } else if (classifier.coding[0].display === "Drug/Device Combination Product Indicator") {
                            if (classifier.text === "True") {
                                drugDeviceComboIndicator = "Yes (C49488)";
                            } else if (classifier.text === "False") {
                                drugDeviceComboIndicator = "No (C49487)";
                            }
                        } else if (classifier.coding[0].display === "Adaptive Trial Design Indicator") {
                            if (classifier.text === "True") {
                                adaptiveTrialIndicator = "Yes (C49488)";
                            } else if (classifier.text === "False") {
                                adaptiveTrialIndicator = "No (C49487)";
                            }
                        }
                    }
                }
            }
            if (m11ResearchStudyResource.condition?.length > 0) {
                populationDiagnosis = simpleDisplayStringFromFHIRCodeableConcept(m11ResearchStudyResource.condition[0]);
            }
            if (m11ResearchStudyResource.extension?.length) {
                for (const extension of m11ResearchStudyResource.extension) {
                    if (extension.url === "http://hl7.org/fhir/StructureDefinition/artifact-useContext" &&
                        extension.valueUsageContext?.code?.code === "age" &&
                        extension.valueUsageContext.valueRange) {
                        let ageRange = extension.valueUsageContext.valueRange;
                        if (ageRange.low) {
                            minimumAge = displayStringFromFHIRQuantity(ageRange.low);
                        }
                        if (ageRange.high) {
                            maximumAge = displayStringFromFHIRQuantity(ageRange.high);
                        }
                    }
                }
            }
            let section112Xhtml = "<div xmlns=\"http://www.w3.org/1999/xhtml\">";
            section112Xhtml += "<p>Key aspects of the trial design are summarised below.</p>";
            section112Xhtml += "<table><tbody><tr><td><p><strong>Intervention:</strong> ";
            if (productCodes.length > 0) {
                section112Xhtml += productCodes.join("</p><p>");
            }
            if (productCodes.length > 0 && productNames.length > 0) {
                section112Xhtml += "</p><p>";
            }
            if (productNames.length > 0) {
                section112Xhtml += productNames.join("</p><p>");
            }
            section112Xhtml += "</p></td>";
            section112Xhtml += "<td><strong>Population Type:</strong> " + populationType + "</td></tr>";
            section112Xhtml += "<tr><td><strong>Intervention Model:</strong> " + interventionModel + "</td>";
            section112Xhtml += "<td><strong>Population Diagnosis or Condition:</strong> " +
                populationDiagnosis + "</td></tr>";
            section112Xhtml += "<tr><td><strong>Control Type:</strong> " + controlType + "</td>";
            section112Xhtml += "<td><p><strong>Population Age:</strong> Minimum: " +
                minimumAge + "</p><p>               Maximum: " + maximumAge + "</p></td></tr>";
            section112Xhtml += "<tr><td><strong>Control Description:</strong> " + controlDescription + "</td>";
            section112Xhtml += "<td><p><strong>Site Distribution and Geographic Scope:</strong></p><p>" +
                siteDistribution + "</p><p>" +
                geographicScope + "</p></td></tr>";
            section112Xhtml += "<tr><td><p><strong>Intervention Assignment Method:</strong></p><p>" +
                interventionAssignmentMethod + "</p>" +
                (randomisationType && "<p>" + randomisationType + "</p>") +
                "<p><strong>Stratification</strong> " + stratificationIndicator + "</p></td>";
            section112Xhtml += "<td><strong>Master Protocol:</strong> " +
                masterProtocolIndicator + "</td></tr>";
            section112Xhtml += "<tr><td><p><strong>Drug/Device Combination Product Indicator:</strong></p><p>" +
                drugDeviceComboIndicator + "</p></td>";
            section112Xhtml += "<td><p><strong>Adaptive Trial Design:</strong></p><p>" +
                adaptiveTrialIndicator + "</p></td></tr>";
            section112Xhtml += "</tbody></table></div>";
            setSection112State({
                "status": "generated", "section112Xhtml": section112Xhtml, "loaded": true
            });
            setStartingSection112State({
                "status": "empty", "section112Xhtml": "", "loaded": true
            });
        }
    }, [initialCalculationNeeded]);

    useEffect(() => {
        if (m11ReportContainerFoi && !isNaN(m11ReportContainerFoi)) {
            loadResource(m11ReportContainerFoi, "M11ReportContainer", apiToken, dispatch);
        }
    }, [m11ReportContainerFoi]);

    useEffect(() => {
        if (m11ReportContainerResource && Object.keys(m11ReportContainerResource).length > 0) {
            if (m11ReportContainerResource.section?.[1]?.entry?.length) {
                let theFoi = getIdFromReference(m11ReportContainerResource.section[1].entry[0]);
                if (theFoi && !isNaN(theFoi) && theFoi !== m11ReportSection01Foi) {
                    dispatch(m11ReportSection01FoiChanged(theFoi));
                } else {
                    setInitialCalculationNeeded(true);
                }
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ReportContainerResource]);

    useEffect(() => {
        if (m11ReportSection01Foi && !isNaN(m11ReportSection01Foi)) {
            loadResource(m11ReportSection01Foi, "M11ReportSection01", apiToken, dispatch);
        }
    }, [m11ReportSection01Foi]);

    useEffect(() => {
        if (m11ReportSection01Resource && Object.keys(m11ReportSection01Resource).length > 0) {
            if (m11ReportSection01Resource.section?.[0]?.section?.[0]?.section?.[1]?.text?.div &&
                m11ReportSection01Resource.section[0].section[0].section[1].text.status !== "empty") {
                let startingData = {
                    "status": m11ReportSection01Resource.section[0].section[0].section[1].text.status,
                    "section112Xhtml": m11ReportSection01Resource.section[0].section[0].section[1].text.div,
                    "loaded": true
                };
                setSection112State(startingData);
                setStartingSection112State(startingData);
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ReportSection01Resource]);

    return <div>
        <h3>Review Overall Design</h3>
        {!(m11ReportSection01Resource?.id || m11ReportContainerResource?.id) &&
            <h1 style={{ color: "red" }}>Please complete Step 5 (Complete Title Page) before filling in M11 Report Section details.</h1>
        }
        {section112State.loaded && <>
            {section112State.section112Xhtml ?
                <>
                    <h4 style={{ marginBottom: "0px" }}>Revise the Section 1.1.2 Overall Design summary as needed.</h4>
                    <TextDivEntry elementName="section112Xhtml"
                        startingValue={section112State.section112Xhtml}
                        setResourceState={setSection112State} />
                </>
                :
                <div>
                    <p>Something is wrong.</p>
                </div>
            }
        </>}
    </div >
});

const M11Step016OverallDesignHelperText = <div>
    <h3>Review Overall Design</h3>
    <div style={{ textAlign: "left" }}>
        <p>Review the auto-generated summary from previously submitted data. This will be the content for Section 1.1.2 Overall Design.</p>
        <p>Revise content as desired.</p>
        <p>Further clarification</p>
        <div style={{ marginLeft: "24px" }}>
            <p>Control Description: if active comparator or low dose, pick nonproprietary name or International Nonproprietary Name; indicate "Not applicable” if not applicable.</p>
            <p>Intervention Assignment Method: If applicable, describe the type of randomisation to be used (e.g., simple randomisation, block randomisation). If applicable,, describe the other intervention assignment method.</p>
            <p>Population Diagnosis or Condition: MedDRA Preferred Term(s) or indicate “other” and describe.</p>
            <p>Population Age: for trials in which multiple age ranges may be eligible (e.g., a younger cohort and an older cohort), indicate the minimum and maximum ages for the trial overall, with an additional comment for any excluded age ranges.</p>
        </div>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div>

// eslint-disable-next-line no-empty-pattern
const M11Step016OverallDesignValidationCheck = ({ }) => {

  const apiToken = useSelector(selectCurrentUserApiToken);
  const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

  const [validationCheckState, setValidationCheckState] = useState({});

  useEffect(() => {
    if (m11ResearchStudyFoi) {
      validateStep(apiToken, 16, m11ResearchStudyFoi,
        setValidationCheckState)
    }
  }, [m11ResearchStudyFoi]);

  return <div>{validationCheckState?.success && <div>
        <h4>Step 16 (Review Overall Design) {validationCheckState.step16numberOfChecks} issues checked. {validationCheckState.step16numberPassed} passed. {validationCheckState.step16numberFailed} failed.</h4>
        <p>Section 1.1.2 heading is 'Overall Design': {validationCheckState.resultCheckSection112Heading}</p>
        <p>Section 1.1.2 has content: {validationCheckState.resultCheckSection112Content}</p>
        <p>Section 1.1.2 content includes 'Key aspects of the trial design are summarised below.': {validationCheckState.resultCheckSection112ContentOpening}</p>
        <p>Section 1.1.2 content includes 'Intervention:': {validationCheckState.resultCheckSection112ContentIntervention}</p>
        <p>Section 1.1.2 content includes 'Intervention Model:': {validationCheckState.resultCheckSection112ContentInterventionModel}</p>
        <p>Section 1.1.2 content includes 'Population Type:': {validationCheckState.resultCheckSection112ContentPopulationType}</p>
        <p>Section 1.1.2 content includes 'Control Type:': {validationCheckState.resultCheckSection112ContentControlType}</p>
        <p>Section 1.1.2 content includes 'Population Diagnosis or Condition:': {validationCheckState.resultCheckSection112ContentDiagnosis}</p>
        <p>Section 1.1.2 content includes 'Control Description:': {validationCheckState.resultCheckSection112ContentControlDescription}</p>
        <p>Section 1.1.2 content includes 'Population Age:': {validationCheckState.resultCheckSection112ContentPopulationAge}</p>
        <p>Section 1.1.2 content includes 'Minimum:': {validationCheckState.resultCheckSection112ContentMinimum}</p>
        <p>Section 1.1.2 content includes 'Maximum:': {validationCheckState.resultCheckSection112ContentMaximum}</p>
        <p>Section 1.1.2 content includes 'Intervention Assignment Method:': {validationCheckState.resultCheckSection112ContentInterventionAssignmentMethod}</p>
        <p>Section 1.1.2 content includes 'Stratification': {validationCheckState.resultCheckSection112ContentStratification}</p>
        <p>Section 1.1.2 content includes 'Site Distribution and Geographic Scope:': {validationCheckState.resultCheckSection112ContentSite}</p>
        <p>Section 1.1.2 content includes 'Master Protocol:': {validationCheckState.resultCheckSection112ContentMasterProtocol}</p>
        <p>Section 1.1.2 content includes 'Drug/Device Combination Product Indicator:': {validationCheckState.resultCheckSection112ContentDrugDevice}</p>
        <p>Section 1.1.2 content includes 'Adaptive Trial Design:': {validationCheckState.resultCheckSection112ContentAdaptive}</p>
    </div>}  </div>
}

export default M11Step016OverallDesign;
export { M11Step016OverallDesignHelperText, M11Step016OverallDesignValidationCheck };